import React, { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { HERO_SLIDES } from "@/lib/data";

export default function HeroCarousel() {
  // The Autoplay plugin instance is kept in a ref so we can stop/reset it on
  // pointer enter/leave to pause the slideshow while the shopper is reading.
  const autoplayRef = useRef(
    Autoplay({
      delay: 5500,
      stopOnInteraction: false,
      // Pause while the mouse is over the carousel — the built-in
      // stopOnMouseEnter is respected by the plugin's default handlers.
      stopOnMouseEnter: true,
    }),
  );
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [autoplayRef.current]);
  const [selected, setSelected] = useState(0);

  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);
  const prev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSel = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSel);
    onSel();
  }, [emblaApi]);

  // Explicit mouse handlers as belt-and-suspenders on top of Embla Autoplay's
  // stopOnMouseEnter — some older browsers don't fire the plugin's internal
  // pointer handler when the cursor enters a nested <Link>.
  const pause = useCallback(() => autoplayRef.current?.stop?.(), []);
  const resume = useCallback(() => autoplayRef.current?.reset?.(), []);

  return (
    // Hero band renders edge-to-edge (no dyntra-container padding, no top
    // margin, no rounded corners) so the page's grey `#F8F9FA` background
    // never shows as a boundary around the banner. Prev/next arrows sit on
    // top of the image with their own absolute positioning, so removing the
    // container's horizontal padding doesn't affect their placement.
    <section className="w-full">
      <div
        data-testid="hero-carousel"
        className="relative overflow-hidden bg-[#4B2AAD]"
        onMouseEnter={pause}
        onMouseLeave={resume}
      >
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {HERO_SLIDES.map((s, idx) => (
              <div key={s.id} className="relative min-w-0 flex-[0_0_100%]">
                <Link
                  to={s.cta_primary?.href || "/"}
                  data-testid={`hero-slide-${idx}`}
                  // A-spot aspect matches the shipped artwork exactly
                  // (2172x724 = 3:1) so `object-cover` produces zero crop
                  // and zero letterbox at any viewport. Mobile uses the
                  // same 3:1 ratio to keep proportional layout — the
                  // banner ends up short on small screens (as intended
                  // for a compact mobile hero).
                  className="block relative w-full aspect-[2172/724] overflow-hidden group bg-[#4B2AAD]"
                  aria-label={s.title}
                >
                  <img
                    src={s.image}
                    alt={s.title}
                    loading={idx === 0 ? "eager" : "lazy"}
                    style={{ transitionDuration: "6000ms" }}
                    className="absolute inset-0 h-full w-full object-cover object-center transition-transform ease-out group-hover:scale-[1.02]"
                  />
                  {/* Screen-reader-friendly overlay for CTAs */}
                  <span className="sr-only" data-testid={`hero-slide-${idx}-title`}>{s.title}</span>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Prev / Next */}
        <button
          data-testid="hero-prev"
          onClick={prev}
          className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/25 hover:bg-[#4B2AAD] hover:border-[#4B2AAD] backdrop-blur border border-white/30 flex items-center justify-center transition-colors"
          aria-label="Previous"
        >
          <ChevronLeft className="h-5 w-5 text-white" />
        </button>
        <button
          data-testid="hero-next"
          onClick={next}
          className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/25 hover:bg-[#4B2AAD] hover:border-[#4B2AAD] backdrop-blur border border-white/30 flex items-center justify-center transition-colors"
          aria-label="Next"
        >
          <ChevronRight className="h-5 w-5 text-white" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-dot-${i}`}
              onClick={() => scrollTo(i)}
              className={`slide-dot ${selected === i ? "active" : ""}`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
